import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getIngredients, createIngredient, updateIngredient, deleteIngredient, getSubscription, getIngredientsBekas, createCheckout, getCompositionOverrides, setCompositionOverride } from '../api/client'
import { Plus, Trash2, Pencil, Search, Lock, Star, Loader2, ExternalLink, ChevronRight, X, ListTree } from 'lucide-react'
import toast from 'react-hot-toast'

const ALLERGEN_OPTIONS = ['gluten','rakfelek','tojas','halak','foldimogyoro','szoja','tej','diofele','zeller','mustar','szezam','ken_dioxid','csillagfurt','puhatestuk']
const ALLERGEN_HU: Record<string, string> = {
  gluten:       '1. Glutén',
  rakfelek:     '2. Rákfélék',
  tojas:        '3. Tojás',
  halak:        '4. Hal',
  foldimogyoro: '5. Földimogyoró',
  szoja:        '6. Szója',
  tej:          '7. Tej',
  diofele:      '8. Diófélék',
  zeller:       '9. Zeller',
  mustar:       '10. Mustár',
  szezam:       '11. Szézám',
  ken_dioxid:   '12. Kén-dioxid',
  csillagfurt:  '13. Csillagfürt',
  puhatestuk:   '14. Puhatestűek',
}

const EMPTY = {
  name: '', energy_kcal: 0, energy_kj: 0,
  fat: 0, saturated_fat: 0, carbs: 0, sugars: 0, fiber: 0, protein: 0, salt: 0,
  allergens: [] as string[],
  composition: '',
}

type Tab = 'sajat' | 'rendszer' | 'bekas'

function AllergenBadges({ allergens }: { allergens: string[] }) {
  if (!allergens?.length) return null
  return (
    <div className="flex flex-wrap gap-1">
      {allergens.map(a => (
        <span key={a} className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
          {ALLERGEN_HU[a] || a}
        </span>
      ))}
    </div>
  )
}

function IngredientTable({ items, onDelete, onEdit, onEditComposition, overrides }: {
  items: any[]
  onDelete?: (id: number) => void
  onEdit?: (item: any) => void
  onEditComposition?: (item: any) => void
  overrides?: Record<number, string>
}) {
  if (!items.length) return (
    <div className="text-sm text-gray-400 text-center py-10">Nincs találat</div>
  )
  const hasActions = !!(onDelete || onEdit || onEditComposition)
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Megnevezés</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">kcal</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">kJ</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">Zsír</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">Tel. zsír</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">Szénhidrát</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">Cukrok</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">Rost</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">Fehérje</th>
            <th className="text-right px-3 py-3 font-medium text-gray-600">Só</th>
            <th className="px-3 py-3 font-medium text-gray-600">Allergének</th>
            {hasActions && <th className="px-3 py-3 w-20" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((i: any) => (
            <tr key={i.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-medium">
                <div className="text-gray-900">{i.name}</div>
                {i.display_name && (
                  <div className="text-xs text-brand-600 mt-0.5 truncate max-w-xs">→ címkén: „{i.display_name}”</div>
                )}
                {i.composition && (
                  <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{i.composition}</div>
                )}
              </td>
              <td className="text-right px-3 py-3 text-gray-700">{i.energy_kcal}</td>
              <td className="text-right px-3 py-3 text-gray-500">{i.energy_kj}</td>
              <td className="text-right px-3 py-3 text-gray-600">{i.fat}g</td>
              <td className="text-right px-3 py-3 text-gray-500">{i.saturated_fat}g</td>
              <td className="text-right px-3 py-3 text-gray-600">{i.carbs}g</td>
              <td className="text-right px-3 py-3 text-gray-500">{i.sugars}g</td>
              <td className="text-right px-3 py-3 text-gray-500">{i.fiber}g</td>
              <td className="text-right px-3 py-3 text-gray-600">{i.protein}g</td>
              <td className="text-right px-3 py-3 text-gray-500">{i.salt}g</td>
              <td className="px-3 py-3"><AllergenBadges allergens={i.allergens} /></td>
              {hasActions && (
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    {!i.is_system && onEdit && (
                      <button onClick={() => onEdit(i)} className="text-gray-300 hover:text-brand-500 transition-colors" title="Szerkesztés">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {!i.is_system && onDelete && (
                      <button onClick={() => onDelete(i.id)} className="text-gray-300 hover:text-red-500 transition-colors" title="Törlés">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {i.is_system && onEditComposition && (
                      <button onClick={() => onEditComposition(i)}
                        className={`transition-colors flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg border ${overrides?.[i.id] ? 'text-brand-600 border-brand-200 bg-brand-50' : 'text-gray-400 border-gray-200 hover:text-brand-500 hover:border-brand-200'}`}
                        title="Összetétel megadása">
                        <ListTree className="w-3.5 h-3.5" />
                        {overrides?.[i.id] ? 'Összetétel ✓' : 'Összetétel'}
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function BekasLockedRow({ item }: { item: any }) {
  return (
    <tr className="select-none">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Lock className="w-3 h-3 text-gray-300 shrink-0" />
          <div>
            <div className="text-gray-500 font-medium">{item.name}</div>
            {item.display_name && (
              <div className="text-xs text-gray-300 mt-0.5 truncate max-w-xs">→ címkén: „{item.display_name}”</div>
            )}
            {item.composition && (
              <div className="text-xs text-gray-300 mt-0.5 truncate max-w-xs">{item.composition}</div>
            )}
          </div>
        </div>
      </td>
      <td className="text-right px-3 py-3 text-gray-300">{item.energy_kcal}</td>
      <td className="text-right px-3 py-3 text-gray-300">{item.energy_kj}</td>
      <td className="text-right px-3 py-3 text-gray-300">{item.fat}g</td>
      <td className="text-right px-3 py-3 text-gray-300">{item.saturated_fat}g</td>
      <td className="text-right px-3 py-3 text-gray-300">{item.carbs}g</td>
      <td className="text-right px-3 py-3 text-gray-300">{item.sugars}g</td>
      <td className="text-right px-3 py-3 text-gray-300">{item.fiber}g</td>
      <td className="text-right px-3 py-3 text-gray-300">{item.protein}g</td>
      <td className="text-right px-3 py-3 text-gray-300">{item.salt}g</td>
      <td className="px-3 py-3">
        {item.allergens?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.allergens.map((a: string) => (
              <span key={a} className="text-[10px] bg-gray-100 text-gray-300 px-1.5 py-0.5 rounded font-medium">
                {ALLERGEN_HU[a] || a}
              </span>
            ))}
          </div>
        )}
      </td>
    </tr>
  )
}

export default function IngredientsPage() {
  const [tab, setTab] = useState<Tab>('sajat')
  const [q, setQ] = useState('')
  const [bekasQ, setBekasQ] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState({ ...EMPTY })
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [compModal, setCompModal] = useState<{ id: number; name: string; current: string } | null>(null)
  const [compValue, setCompValue] = useState('')
  const qc = useQueryClient()

  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => getSubscription().then(r => r.data),
  })
  const hasBekas = sub?.bekas_pack || false

  const { data: ingredients = [], isLoading } = useQuery({
    queryKey: ['ingredients', q],
    queryFn: () => getIngredients(q || undefined).then(r => r.data),
  })

  const { data: overrides = {} } = useQuery({
    queryKey: ['composition-overrides'],
    queryFn: () => getCompositionOverrides().then(r => r.data),
  })

  const saveCompositionMut = useMutation({
    mutationFn: ({ id, composition }: { id: number; composition: string }) =>
      setCompositionOverride(id, composition),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['composition-overrides'] })
      toast.success('Összetétel elmentve')
      setCompModal(null)
    },
    onError: () => toast.error('Hiba'),
  })

  function openCompModal(item: any) {
    const current = (overrides as any)[item.id] || item.composition || ''
    setCompValue(current)
    setCompModal({ id: item.id, name: item.name, current })
  }

  const { data: bekasAll = [], isLoading: bekasLoading } = useQuery({
    queryKey: ['bekas-all', bekasQ],
    queryFn: () => getIngredientsBekas(bekasQ || undefined).then(r => r.data),
  })

  const sajat = (ingredients as any[]).filter((i: any) => !i.is_system)
  const rendszer = (ingredients as any[]).filter((i: any) => i.is_system && !i.is_premium)

  const createMut = useMutation({
    mutationFn: () => createIngredient(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingredients'] })
      toast.success('Alapanyag hozzáadva')
      setForm({ ...EMPTY })
      setShowNew(false)
    },
    onError: () => toast.error('Hiba'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: typeof EMPTY }) => updateIngredient(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingredients'] })
      toast.success('Módosítva')
      setForm({ ...EMPTY })
      setEditId(null)
      setShowNew(false)
    },
    onError: () => toast.error('Hiba'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteIngredient(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ingredients'] }); toast.success('Törölve') },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Hiba'),
  })

  function startEdit(item: any) {
    setForm({
      name: item.name,
      energy_kcal: item.energy_kcal ?? 0,
      energy_kj: item.energy_kj ?? 0,
      fat: item.fat ?? 0,
      saturated_fat: item.saturated_fat ?? 0,
      carbs: item.carbs ?? 0,
      sugars: item.sugars ?? 0,
      fiber: item.fiber ?? 0,
      protein: item.protein ?? 0,
      salt: item.salt ?? 0,
      allergens: item.allergens || [],
      composition: item.composition || '',
    })
    setEditId(item.id)
    setShowNew(true)
  }

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.type === 'number' ? +e.target.value : e.target.value }))

  function toggleAllergen(a: string) {
    setForm(f => ({
      ...f,
      allergens: f.allergens.includes(a) ? f.allergens.filter(x => x !== a) : [...f.allergens, a],
    }))
  }

  async function buyBekas() {
    setCheckoutLoading(true)
    try {
      const res = await createCheckout('bekas_annual')
      window.location.href = res.data.checkout_url
    } catch {
      toast.error('Hiba a fizetési felület megnyitásakor')
      setCheckoutLoading(false)
    }
  }

  const tabs: { key: Tab; label: string; count: number | string }[] = [
    { key: 'sajat', label: 'Saját', count: sajat.length },
    { key: 'rendszer', label: 'Rendszer', count: rendszer.length },
    { key: 'bekas', label: 'Békás Kft. Prémium', count: (bekasAll as any[]).length || 182 },
  ]

  return (
    <div>
      {/* Összetétel override modal */}
      {compModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative">
            <button onClick={() => setCompModal(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={20} /></button>
            <h2 className="text-base font-bold mb-1" style={{ color: '#1E2D27' }}>Összetétel megadása</h2>
            <p className="text-sm text-gray-500 mb-1">{compModal.name}</p>
            <p className="text-xs text-gray-400 mb-4">Ez csak a te cégednek érvényes. A gyártó által megadott összetevőlistát írd be — megjelenik a címkén zárójelben.</p>
            <textarea
              className="input resize-none w-full"
              rows={3}
              placeholder="pl. cukor, kakaóvaj, kakaómassza, SZÓJALECITIN (E322), természetes vaníliaaroma"
              value={compValue}
              onChange={e => setCompValue(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2 mt-4">
              <button
                className="btn-primary text-sm flex items-center gap-1.5"
                onClick={() => saveCompositionMut.mutate({ id: compModal.id, composition: compValue })}
                disabled={saveCompositionMut.isPending}
              >
                {saveCompositionMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Mentés
              </button>
              {compValue && (
                <button className="btn-secondary text-sm" onClick={() => setCompValue('')}>Törlés</button>
              )}
              <button className="btn-secondary text-sm" onClick={() => setCompModal(null)}>Mégse</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Alapanyagok</h1>
          <p className="text-sm text-gray-500 mt-0.5">Tápértékek per 100g — EU 1169/2011</p>
        </div>
        {tab === 'sajat' && (
          <button className="btn-primary flex items-center gap-2 text-sm" onClick={() => setShowNew(v => !v)}>
            <Plus className="w-4 h-4" /> Új alapanyag
          </button>
        )}
      </div>

      {/* Tabok */}
      <div className="flex gap-0.5 border-b border-gray-200 mb-5 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? 'border-brand-500 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t.key === 'bekas' && !hasBekas && <Lock className="w-3.5 h-3.5 text-amber-500" />}
            {t.key === 'bekas' && hasBekas && <Star className="w-3.5 h-3.5 text-amber-500" />}
            {t.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              tab === t.key ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-500'
            }`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Keresősáv — Saját / Rendszer */}
      {tab !== 'bekas' && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9 text-sm" placeholder="Keresés..." value={q} onChange={e => setQ(e.target.value)} />
        </div>
      )}

      {/* ── Saját tab ── */}
      {tab === 'sajat' && (
        <>
          {showNew && (
            <div className="card p-5 mb-5 border-brand-200">
              <h3 className="font-semibold text-gray-800 mb-4">{editId ? 'Alapanyag szerkesztése' : 'Új alapanyag (per 100g)'}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                <div className="sm:col-span-2 md:col-span-3">
                  <label className="label">Megnevezés *</label>
                  <input className="input" value={form.name} onChange={set('name')} />
                </div>
                {[
                  ['Energia (kcal)', 'energy_kcal'], ['Energia (kJ)', 'energy_kj'],
                  ['Zsír (g)', 'fat'], ['Telített zsír (g)', 'saturated_fat'],
                  ['Szénhidrát (g)', 'carbs'], ['Cukrok (g)', 'sugars'],
                  ['Rost (g)', 'fiber'], ['Fehérje (g)', 'protein'], ['Só (g)', 'salt'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className="label">{label}</label>
                    <input className="input" type="number" step="0.01"
                      value={(form as any)[key]} onChange={set(key)} />
                  </div>
                ))}
              </div>
              <div className="mb-4">
                <label className="label">Allergének</label>
                <div className="flex flex-wrap gap-2">
                  {ALLERGEN_OPTIONS.map(a => (
                    <button key={a} type="button" onClick={() => toggleAllergen(a)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                        form.allergens.includes(a)
                          ? 'bg-amber-400 border-amber-500 text-amber-900'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300'
                      }`}>
                      {ALLERGEN_HU[a]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="label">
                  Összetétel
                  <span className="text-gray-400 font-normal text-xs ml-1">— összetett alapanyagnál (pl. csokoládé, margarin). Megjelenik a címkén zárójelben.</span>
                </label>
                <textarea
                  className="input resize-none"
                  rows={2}
                  placeholder="pl. cukor, kakaóvaj, kakaómassza, SZÓJALECITIN, természetes vaníliaaroma"
                  value={form.composition}
                  onChange={e => setForm(f => ({ ...f, composition: e.target.value }))}
                />
              </div>
              <div className="flex gap-2">
                <button className="btn-primary text-sm"
                  onClick={() => editId ? updateMut.mutate({ id: editId, data: form }) : createMut.mutate()}
                  disabled={!form.name || createMut.isPending || updateMut.isPending}>
                  {editId ? 'Mentés' : 'Hozzáadás'}
                </button>
                <button className="btn-secondary text-sm" onClick={() => { setShowNew(false); setEditId(null); setForm({ ...EMPTY }) }}>Mégse</button>
              </div>
            </div>
          )}
          {isLoading ? (
            <div className="text-gray-400 text-sm text-center py-10">Betöltés...</div>
          ) : (
            <div className="card overflow-hidden">
              <IngredientTable items={sajat} onDelete={(id) => deleteMut.mutate(id)} onEdit={startEdit} />
              {sajat.length === 0 && !showNew && (
                <div className="text-center py-10">
                  <p className="text-sm text-gray-500 mb-3">Még nincs saját alapanyag</p>
                  <button className="btn-primary text-sm" onClick={() => setShowNew(true)}>
                    <Plus className="w-4 h-4 inline mr-1" />Első alapanyag hozzáadása
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Rendszer tab ── */}
      {tab === 'rendszer' && (
        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="text-gray-400 text-sm text-center py-10">Betöltés...</div>
          ) : (
            <IngredientTable items={rendszer} onEditComposition={openCompModal} overrides={overrides as any} />
          )}
        </div>
      )}

      {/* ── Békás Prémium tab ── */}
      {tab === 'bekas' && (
        <div>
          {/* Feloldó banner — csak ha nincs előfizetés */}
          {!hasBekas && (
            <div className="rounded-2xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-400 flex items-center justify-center shrink-0">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-sm">Békás Kft. Prémium — 182 cukrászati alapanyag</div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Tápértékek, allergének, összetétel adatok — azonnal receptekbe adható.{' '}
                    <span className="font-semibold text-amber-700">14 900 Ft / év</span>
                  </p>
                </div>
              </div>
              <button
                onClick={buyBekas}
                disabled={checkoutLoading}
                className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-amber-400 hover:bg-amber-500 text-white transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {checkoutLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Átirányítás...</>
                  : <><ExternalLink className="w-4 h-4" /> Feloldás — 14 900 Ft/év</>
                }
              </button>
            </div>
          )}

          {/* Keresősáv */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="input pl-9 text-sm"
              placeholder="Keresés a Békás termékek között..."
              value={bekasQ}
              onChange={e => setBekasQ(e.target.value)}
              disabled={!hasBekas}
            />
          </div>

          {/* Termék lista */}
          <div className="card overflow-hidden">
            {!hasBekas && (
              <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
                <span className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
                  <Lock className="w-3 h-3" />
                  Előnézet — a termékek láthatók, de receptbe csak előfizetéssel adhatók hozzá
                </span>
                <button onClick={buyBekas} disabled={checkoutLoading}
                  className="text-xs font-semibold text-amber-600 hover:text-amber-800 flex items-center gap-1 transition-colors">
                  Feloldás <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {bekasLoading ? (
              <div className="text-gray-400 text-sm text-center py-10">Betöltés...</div>
            ) : hasBekas ? (
              <IngredientTable items={bekasAll as any[]} onEditComposition={openCompModal} overrides={overrides as any} />
            ) : (
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[900px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Megnevezés</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">kcal</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">kJ</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Zsír</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Tel. zsír</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Szénhidrát</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Cukrok</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Rost</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Fehérje</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Só</th>
                    <th className="px-3 py-3 font-medium text-gray-600">Allergének</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(bekasAll as any[]).map((item: any) => (
                    <BekasLockedRow key={item.id} item={item} />
                  ))}
                </tbody>
              </table></div>
            )}

            {!bekasLoading && (bekasAll as any[]).length === 0 && (
              <div className="text-sm text-gray-400 text-center py-10">Nincs találat</div>
            )}
          </div>

          {/* Lábléc zárolva állapotban */}
          {!hasBekas && (bekasAll as any[]).length > 0 && (
            <div className="mt-3 text-center">
              <p className="text-xs text-gray-400 mb-2">
                Mind a {(bekasAll as any[]).length} termék látható — receptbe adáshoz feloldás szükséges
              </p>
              <button
                onClick={buyBekas}
                disabled={checkoutLoading}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl font-semibold text-sm bg-amber-400 hover:bg-amber-500 text-white transition-colors disabled:opacity-60"
              >
                {checkoutLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Átirányítás...</>
                  : <><Star className="w-4 h-4" /> Békás Prémium feloldása — 14 900 Ft/év</>
                }
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
